import { maxBy } from "lodash";
import React, { FC, useEffect } from "react";
import { toast } from "react-toastify";
import { useImmer } from "use-immer";

import {
  ColorError,
  IgButton,
  IgCopyTextTypography,
  IgPaper,
  IgStatusCircle,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  GenericBillingPlanDto,
  OrganizationSubscriptionDto,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { styled } from "@mui/material/styles";

import { useCancelFreeTrial } from "../../api/organization-subscriptions/cancel-free-trial";
import {
  useGetOrg,
  useGetOrgId,
} from "../../api/organizations/get-organization";
import { usePatchOrganization } from "../../api/organizations/patch-organization";
import { useManualDeploy } from "../../api/plugin-configs/manual-deploy";
import { useAppSubscriptions } from "../../api/subscriptions/get-app-subscriptions";
import { useIsAdmin } from "../../api/user-auths/is-admin";
import { billingSentence } from "../../utils/approveBilling";
import { urlRegex } from "../../utils/constants";
import Loading from "../../utils/Loading";

import { CancelButton } from "./CancelButton";

const PREFIX = "OrganizationInformation";

const classes = {
  flexContainer: `${PREFIX}-flexContainer`,
  dataLabel: `${PREFIX}-dataLabel`,
  sectionLabel: `${PREFIX}-sectionLabel`,
  container: `${PREFIX}-container`,
  scriptContainer: `${PREFIX}-scriptContainer`,
  scriptLabel: `${PREFIX}-scriptLabel`,
};

const StyledPaper = styled(IgPaper)(({ theme }) => ({
  [`& .${classes.flexContainer}`]: {
    display: "flex",
    flexDirection: "row",
  },

  [`& .${classes.dataLabel}`]: {
    fontWeight: "bold",
    marginRight: 20,
  },

  [`& .${classes.sectionLabel}`]: {
    marginTop: 5,
  },

  [`& .${classes.container}`]: {
    marginBottom: 20,
  },

  [`& .${classes.scriptContainer}`]: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 20,
  },

  [`& .${classes.scriptLabel}`]: {
    fontWeight: "bold",
    marginRight: 20,
    alignSelf: "center",
  },
}));

export function getTotalSubscriptionPrice(
  orgSubscriptions: OrganizationSubscriptionDto[]
) {
  const activeSubs = (orgSubscriptions || []).filter(
    (sub) => sub.subscriptionStatus === "active"
  );
  const totalPrice = activeSubs.reduce(
    (acc, sub) => acc + sub.subscriptionPriceUsd,
    0
  );
  const maxInterval = maxBy(activeSubs, (sub) => sub.intervalCount);

  return {
    totalPrice,
    intervalCount: maxInterval?.intervalCount || 1,
    interval: maxInterval?.subscriptionInterval || "monthly",
  };
}

function getSubscriptionStatus(
  organizationSubscriptions: OrganizationSubscriptionDto[]
) {
  if (
    organizationSubscriptions.some((sub) => sub.subscriptionStatus === "active")
  ) {
    return "Active";
  }
  if (
    organizationSubscriptions.some(
      (sub) => sub.subscriptionStatus === "pending"
    )
  ) {
    return "Pending";
  }
  return "Cancelled";
}

const subscriptionStatusToStatusLabel = (
  organizationSubscriptions: OrganizationSubscriptionDto[]
) => {
  if (getSubscriptionStatus(organizationSubscriptions) === "Active") {
    return "success";
  }
  if (getSubscriptionStatus(organizationSubscriptions) === "Pending") {
    return "warning";
  }
  return "danger";
};

export const OrganizationInformation = () => {
  const isAdmin = useIsAdmin();
  const manualDeploy = useManualDeploy();
  const [url, setUrl] = useImmer<string>("");
  const [error, setError] = useImmer<boolean>(false);
  const organizationId = useGetOrgId();
  const organization = useGetOrg({
    refetchOnWindowFocus: "always",
  });
  const patchOrganization = usePatchOrganization();
  const [isFreeTrial, setIsFreeTrial] = useImmer<boolean>(false);

  const cancelFreeTrial = useCancelFreeTrial();

  const appSubscriptions = useAppSubscriptions({
    refetchOnWindowFocus: "always",
  });
  const [appSubscription, setAppSubscription] =
    useImmer<GenericBillingPlanDto | null>(null);

  useEffect(() => {
    setUrl(organization.data.org.domain || "");
    setIsFreeTrial(
      (organization.data.org.freeTrialExpiresAtTs || 0) > Date.now() &&
        getSubscriptionStatus(
          organization.data.org.organizationSubscriptions
        ) === "Active"
    );
  }, []);

  useEffect(() => {
    // set app subscription to the first subscription
    if (
      appSubscriptions.isSuccess &&
      appSubscriptions.data.shopifyPlans.length > 0
    ) {
      setAppSubscription(appSubscriptions.data.shopifyPlans[0]);
    }
  }, [appSubscriptions.isSuccess]);

  const handleChange = (value: string) => {
    setUrl(value);
    if (value.length <= 8 || urlRegex.test(value)) {
      setError(false);
    } else {
      setError(true);
    }
  };

  const handleYesClick = () => {
    patchOrganization.mutate({
      body: {
        domain: url,
      },
      queryParams: undefined,
      pathParams: undefined,
    });
  };

  const handleCancelFreeTrialClick = () => {
    if (appSubscription && appSubscription.provider === "shopify") {
      cancelFreeTrial.mutate({
        body: {
          id: appSubscription.id,
        },
        pathParams: undefined,
        queryParams: undefined,
      });
    }
  };

  return (
    <StyledPaper sx={{ marginRight: "8px" }}>
      <Grid direction={"column"} container rowSpacing={1}>
        <Grid
          container
          item
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Grid item>
            <IgTypography variant="h6" className={classes.sectionLabel}>
              Organization Information
            </IgTypography>
          </Grid>
          {isAdmin ? (
            <Grid item>
              <IgButton
                loading={manualDeploy.isPending}
                variant={"contained"}
                onClick={() => {
                  manualDeploy.mutate();
                }}
              >
                Deploy
              </IgButton>
            </Grid>
          ) : null}
        </Grid>
        <Grid container direction={"row"} item xs={12}>
          <IgTypography className={classes.dataLabel}>Name:</IgTypography>
          <IgTypography>{organization.data.org.name}</IgTypography>
        </Grid>
        <Grid
          container
          direction={"row"}
          item
          flexWrap={"nowrap"}
          alignItems={"center"}
        >
          <Grid item>
            <IgTypography className={classes.dataLabel}>Domain:</IgTypography>
          </Grid>
          <Grid container item direction={"row"}>
            <Grid container item>
              <Grid item flexGrow={10}>
                <IgTextField
                  fullWidth={true}
                  value={url}
                  onChange={(e) => handleChange(e.target.value)}
                />
              </Grid>
              <Grid item sx={{ width: "75px" }}>
                <IgButton
                  onClick={handleYesClick}
                  loading={patchOrganization.isPending}
                  disabled={error}
                >
                  Save
                </IgButton>
              </Grid>
            </Grid>
            <Grid item>
              {error ? (
                <IgTypography color={ColorError.main} sx={{ fontSize: "13px" }}>
                  Invalid URL format
                </IgTypography>
              ) : null}
            </Grid>
          </Grid>
        </Grid>
        <Grid container direction={"row"} item xs={12}>
          <IgTypography className={classes.dataLabel}>ID:</IgTypography>
          <IgTypography>
            <IgCopyTextTypography
              text={organization.data.org.id}
              toastMessage={(text) =>
                toast.success(
                  `Organization ${text.length === 12 ? "Short" : ""} ID Copied`
                )
              }
            />{" "}
            (
            <IgCopyTextTypography
              text={organizationId}
              toastMessage={(text) =>
                toast.success(
                  `Organization ${text.length === 12 ? "Short" : ""} ID Copied`
                )
              }
            />
            )
          </IgTypography>
        </Grid>
        <Grid container direction={"row"} item xs={12}>
          <IgTypography className={classes.dataLabel}>Shop ID:</IgTypography>
          <IgTypography>
            <IgCopyTextTypography
              text={organization.data.org.shopId ?? ""}
              toastMessage={(text) =>
                toast.success(`Organization Shop ID Copied`)
              }
            />
          </IgTypography>
        </Grid>
        <Grid container direction={"row"} item xs={12}>
          <IgTypography className={classes.dataLabel}>Plan:</IgTypography>
          <IgTypography>
            {billingSentence(
              getTotalSubscriptionPrice(
                organization.data.org.organizationSubscriptions
              )
            )}
          </IgTypography>
        </Grid>
        <Grid container direction={"row"} item xs={12}>
          <IgTypography className={classes.dataLabel}>
            Billing Status:
          </IgTypography>
          <IgTypography>
            {getSubscriptionStatus(
              organization.data.org.organizationSubscriptions
            )}
          </IgTypography>
          <IgStatusCircle
            status={subscriptionStatusToStatusLabel(
              organization.data.org.organizationSubscriptions
            )}
          />
        </Grid>
        <Grid item xs={3}>
          {isFreeTrial && appSubscription && (
            <CancelButton
              keyId={"cancel-free-trial"}
              hoverText={
                "Please reach out to sales@intelligems.io if you'd like to extend your trial."
              }
              cancelText={"Cancel free trial"}
              handleYesClick={handleCancelFreeTrialClick}
              isLoading={cancelFreeTrial.isPending}
              yesMessage={"Cancel free trial"}
            />
          )}
        </Grid>
      </Grid>
    </StyledPaper>
  );
};
